import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { StatsigProvider } from '@statsig/react-bindings';

import { CHANNELS } from '@constants';
import { switchToBaseUser, switchUserBroadcastSucceeded, userFetch } from '@redux/slices/user';
import { Announcement, LoaderPage, Notification } from '@utilities';

import Footer from '../../components/Footer';
import StatsigLoadingBoundary from '../../components/StatsigLoadingBoundary';
import Header from './components/Header';
import Maintenance from './components/Maintenance';
import ModalOnboarding from './components/ModalOnboarding';
import Router from './components/Router';
import StatsigUserSynchronizer from './components/StatsigUserSynchronizer';

import styles from './_index.module.scss';

export const Dashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const baseProxySwitchChannel = useRef(null);
  const [isStatsigLoading, setIsStatsigLoading] = useState(true);

  const user = useSelector((state) => state?.user);
  const { data = {}, isAuthenticating, isBaseProxySwitched, isLoading } = user;

  const { baseAccountUser, clientInfo, email, firstName, id, lastName, permissions, routes } = data;

  const baseAccountId = baseAccountUser?.id;
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  const isNarrativesRoute = ['/dashboard/narratives', '/dashboard/nexa'].includes(
    location.pathname
  );

  const statsigUser = useMemo(() => {
    if (!id) {
      return null;
    }

    return {
      custom: {
        client_id: clientInfo?.id,
        client_name: clientInfo?.name,
        name: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
      },
      email,
      userID: String(id),
    };
  }, [clientInfo?.id, clientInfo?.name, email, firstName, id, lastName]);

  useEffect(() => {
    dispatch(userFetch());

    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      return undefined;
    }

    try {
      const channel = new BroadcastChannel(CHANNELS.baseProxySwitch);

      channel.onmessage = () => {
        channel.close();
        window.location.reload();
      };

      baseProxySwitchChannel.current = channel;
    } catch (error) {
      console.error('Error initializing BroadcastChannel:', error);
    }

    return () => {
      baseProxySwitchChannel.current?.close();
      baseProxySwitchChannel.current = null;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isBaseProxySwitched) {
      return;
    }

    baseProxySwitchChannel.current?.postMessage({ reload: true });
    dispatch(switchUserBroadcastSucceeded());
  }, [dispatch, isBaseProxySwitched]);

  const isUserLoading = isAuthenticating || isLoading || !routes?.length;
  const isDashboardLoading = isUserLoading || isStatsigLoading;

  if (!isUserLoading && isMaintenanceMode && !permissions?.canBypassMaintenance) {
    return <Maintenance />;
  }

  return (
    <>
      {isDashboardLoading && <LoaderPage />}
      {!isUserLoading && (
        <StatsigProvider
          options={{
            environment: { tier: import.meta.env.VITE_ENVIRONMENT },
          }}
          sdkKey={import.meta.env.VITE_STATSIG}
          user={statsigUser}
        >
          <StatsigUserSynchronizer statsigUser={statsigUser} />
          <StatsigLoadingBoundary
            isVisible={!isDashboardLoading}
            onLoadingChange={setIsStatsigLoading}
          >
            <div className={styles['dashboard']}>
              {isMaintenanceMode && (
                <Announcement
                  header="Maintenance Mode"
                  isCentered
                  text="You are currently bypassing Maintenance Mode"
                  variant="warn"
                />
              )}
              {baseAccountId && (
                <Announcement
                  className={styles['dashboard-proxy']}
                  header="Client Account"
                  isCentered
                  text={
                    <>
                      <span>You are logged in as {email} - </span>
                      <button
                        onClick={() => dispatch(switchToBaseUser({ baseAccountId, navigate }))}
                      >
                        Switch to Personal Account
                      </button>
                    </>
                  }
                  variant="info"
                />
              )}
              <Header routes={routes} />
              <main id="dashboard">
                <Router />
              </main>
              {!isNarrativesRoute && <Footer />}
              <Notification />
              <ModalOnboarding />
            </div>
          </StatsigLoadingBoundary>
        </StatsigProvider>
      )}
    </>
  );
};

export default Dashboard;
